import { padding, percent, px } from "csx";
import { Content, Horizontal, HorizontalSpacer } from "gls/lib";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { style } from "typestyle";
import { Icon } from "@/shared/components/icon/Icon";

const AccordionContext = createContext<{
  openIds: (string | number)[];
  toggle: (id: number | string) => void;
}>({
  openIds: [],
  toggle: (id: number | string) => {},
});

interface AccordionProps {
  allowsMultiple?: boolean;
  initialOpenIds?: (string | number)[];
}

export const Accordion: FCWithChildren<AccordionProps> = React.memo(
  ({ allowsMultiple, initialOpenIds, children }) => {
    const [openIds, setOpenIds] = useState<(string | number)[]>(
      initialOpenIds || [],
    );

    const handleSetId = useCallback(
      (id: string | number) => {
        setOpenIds((ids) => {
          if (ids.includes(id)) {
            return allowsMultiple ? ids.filter((el) => el !== id) : [];
          } else {
            return allowsMultiple ? [...ids, id] : [id];
          }
        });
      },
      [setOpenIds, allowsMultiple],
    );

    return (
      <AccordionContext.Provider
        value={useMemo(
          () => ({
            openIds,
            toggle: handleSetId,
          }),
          [openIds, handleSetId],
        )}
      >
        {children}
      </AccordionContext.Provider>
    );
  },
);

export const AccordionHeader: FCWithChildren<{
  id: string | number;
  hideCarret?: boolean;
  forceOpen?: boolean;
}> = React.memo(({ id, hideCarret, children, forceOpen }) => {
  const { toggle, openIds } = useContext(AccordionContext);

  const isOpen = openIds.includes(id) || forceOpen;
  return (
    <Horizontal
      spacing={0}
      verticalAlign="center"
      sizing="stretch"
      width={percent(100)}
    >
      <Horizontal sizing="stretch">{children}</Horizontal>
      {!hideCarret && (
        <>
          <HorizontalSpacer space={px(10)} />
          <Content
            data-testid={`accordion-toggle-${isOpen ? "angleUp" : "angleDown"}`}
          >
            <Icon
              icon={isOpen ? "angleUp" : "angleDown"}
              onClick={(e) => {
                toggle(id);
                e.stopPropagation();
              }}
              className={style({
                padding: padding(px(10), px(5)),
                cursor: "pointer",
              })}
            />
          </Content>
        </>
      )}
    </Horizontal>
  );
});

export const AccordionContent: FCWithChildren<{
  id: string | number;
  renderConditionaly?: boolean;
  forceOpen?: boolean;
}> = React.memo(({ id, children, renderConditionaly, forceOpen }) => {
  const { openIds } = useContext(AccordionContext);

  const isOpen = openIds?.includes(id) || forceOpen;

  return (
    <Content
      role={"accordion-content-" + id}
      className={style({
        display: isOpen || renderConditionaly ? "flex" : "none !important",
      })}
    >
      {((renderConditionaly && isOpen) || !renderConditionaly) && children}
    </Content>
  );
});

export const useAccordionContext = () => {
  return useContext(AccordionContext);
};
